import { Link } from "react-router-dom";
import { ArrowRight, Building2, User, Target, MapPin, RefreshCw, ShoppingBag, Ear, PenTool, Code2, Rocket } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { FinalCta } from "@/components/FinalCta";
import { useLanguage } from "../i18n";

const SOLUTIONS = [
  { icon: Building2, title: "Siti web aziendali", desc: "Per presentare la tua impresa, i servizi e i valori del brand in modo professionale." },
  { icon: User, title: "Siti per liberi professionisti", desc: "Per raccontare chi sei, cosa fai e farti trovare dai clienti giusti." },
  { icon: Target, title: "Landing page", desc: "Pagine mirate per campagne, lanci di servizi e raccolta di contatti." },
  { icon: MapPin, title: "Siti web per attività locali", desc: "Per farti trovare da chi ti cerca nella tua zona, con percorsi di contatto semplici." },
  { icon: RefreshCw, title: "Restyling di siti esistenti", desc: "Per rinnovare grafica, contenuti ed esperienza del tuo sito attuale." },
  { icon: ShoppingBag, title: "Siti e-commerce", desc: "Disponibili su richiesta: valutiamo insieme la soluzione più adatta alla tua attività." },
];

const PROCESS = [
  { icon: Ear, title: "Ascolto", desc: "Partiamo dalle tue esigenze, dai tuoi obiettivi e dal tuo pubblico." },
  { icon: PenTool, title: "Progettazione", desc: "Definiamo struttura, contenuti e design del sito, condividendo ogni scelta." },
  { icon: Code2, title: "Sviluppo", desc: "Realizziamo il sito con cura, testandolo su desktop, tablet e smartphone." },
  { icon: Rocket, title: "Lancio", desc: "Pubblichiamo il sito e restiamo al tuo fianco per i prossimi passi." },
];

const PROJECTS = [
  {
    img: "/assets/hero-alsera.jpg",
    name: "Vetrina Moderna",
    type: "Sito vetrina",
    desc: "Concept di sito vetrina responsive: la stessa esperienza curata su desktop e smartphone.",
  },
  {
    img: "https://images.unsplash.com/photo-1645021785273-140b720bb63f?crop=entropy&cs=srgb&fm=jpg&q=85",
    name: "Studio Aurora",
    type: "Salone & beauty",
    desc: "Concept di sito per un salone di bellezza: servizi in evidenza, prenotazione semplice e contatti immediati.",
  },
  {
    img: "https://images.unsplash.com/photo-1530435460869-d13625c69bbf?crop=entropy&cs=srgb&fm=jpg&q=85",
    name: "Bottega del Gusto",
    type: "Ristorante & café",
    desc: "Concept di sito per un ristorante o café: menù, informazioni e contatto diretto in primo piano.",
  },
  {
    img: "https://images.pexels.com/photos/8533390/pexels-photo-8533390.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    name: "Officina Digitale",
    type: "Sito aziendale",
    desc: "Concept di sito per una piccola impresa: servizi, casi di lavoro e richiesta preventivo guidata.",
  },
];

export default function SitiWeb() {
  const { t } = useLanguage();
  return (
    <>
      <Seo
        title={`${t("Siti web")} — Alsera Studio`}
        description={t("Progettiamo siti web moderni, veloci e professionali, pensati per valorizzare il tuo brand, presentare i tuoi servizi e aiutarti a trovare nuovi clienti.")}
      />
      <section className="noise-overlay relative overflow-hidden bg-acqua/40 pt-32 pb-20 sm:pt-40" data-testid="sitiweb-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Siti web")}</p>
            <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.1] tracking-tight text-antracite sm:text-5xl">
              {t("Un sito che lavora per il tuo obiettivo.")}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-antracite/70 sm:text-lg">
              {t("Progettiamo siti web moderni, veloci e professionali, pensati per valorizzare il tuo brand, presentare i tuoi servizi e aiutarti a trovare nuovi clienti.")}
            </p>
            <Link to="/contatti" className="btn-primary mt-8" data-testid="sitiweb-cta">
              {t("Richiedi il tuo sito web")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-28" data-testid="sitiweb-soluzioni">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Soluzioni")}</p>
            <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Ogni attività ha il suo sito.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {SOLUTIONS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="group card-soft h-full p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-petrolio/10" data-testid={`soluzione-${i}`}>
                  <div className="mb-5 inline-flex rounded-2xl bg-acqua p-3 text-petrolio transition-colors duration-500 group-hover:bg-petrolio group-hover:text-avorio">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight text-antracite">{t(s.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-antracite/65">{t(s.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="noise-overlay bg-acqua/45 py-24 sm:py-28" data-testid="sitiweb-processo">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Come lavoriamo")}</p>
            <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Un percorso chiaro, in quattro fasi.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div data-testid={`processo-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-display text-3xl font-extrabold text-salvia">{String(i + 1).padStart(2, "0")}</span>
                    <p.icon className="h-5 w-5 text-petrolio" />
                  </div>
                  <h3 className="mt-3 text-lg font-bold tracking-tight text-antracite">{t(p.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-antracite/65">{t(p.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 sm:py-28" data-testid="sitiweb-portfolio">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Portfolio")}</p>
            <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Le idee prendono forma.")}
            </h2>
            <p className="mt-4 max-w-xl text-base text-antracite/70">
              {t("Concept dimostrativi che mostrano stili e soluzioni diverse: il tuo progetto sarà costruito su misura per te.")}
            </p>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {PROJECTS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.07}>
                <article className="group card-soft overflow-hidden transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-petrolio/10" data-testid={`sitiweb-project-${i}`}>
                  <div className="relative overflow-hidden">
                    <img
                      src={p.img}
                      alt={`${p.name} — ${t(p.type)}`}
                      loading="lazy"
                      className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-avorio/90 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.14em] text-petrolio backdrop-blur">
                      {t("Concept — design dimostrativo")}
                    </span>
                  </div>
                  <div className="p-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-petrolio/70">{t(p.type)}</p>
                    <h3 className="mt-1.5 text-lg font-bold tracking-tight text-antracite">{p.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-antracite/65">{t(p.desc)}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinalCta />
    </>
  );
}
